/**
 * Custom hook for Tydro Protocol integration
 * Handles lending, borrowing, and health factor calculations
 */

import { useState, useEffect, useCallback } from 'react';
import { useAccount, usePublicClient, useWalletClient } from 'wagmi';
import { parseUnits, formatUnits, Address, getAddress } from 'viem';
import { ethers } from 'ethers'; // Ethers v5 - main package for Aave SDK compatibility
import { UiPoolDataProvider } from '@aave/contract-helpers';

// Tydro Protocol Addresses on Ink (from official docs: docs.tydro.com/resources/addresses)
const TYDRO_POOL_ADDRESS = getAddress('0x2816cf15F6d2A220E789aA011D5EE4eB6c47FEbA'); // ✅ Main Tydro Pool
const WETH_ADDRESS = getAddress('0x4200000000000000000000000000000000000006'); // ✅ Wrapped ETH on Ink
const USDT0_ADDRESS = getAddress('0x0200c29006150606b650577bbe7b6248f58470c1'); // ✅ USDT0 (Tronix USDT)
const KBTC_ADDRESS = getAddress('0x73E0C0d45E048D25Fc26Fa3159b0aA04BfA4Db98'); // ✅ kBTC (Kraken Wrapped Bitcoin)
const WETH_GATEWAY = getAddress('0xDe090EfCD6ef4b86792e2D84E55a5fa8d49D25D2'); // ✅ WETHGateway for native ETH operations
const UI_POOL_DATA_PROVIDER = getAddress('0x39bc1bfDa2130d6Bb6DBEfd366939b4c7aa7C697'); // ✅ UI data provider for protocol stats
const POOL_ADDRESSES_PROVIDER = getAddress('0x4172E6aAEC070ACB31aaCE343A58c93E4C70f44D'); // ✅ Pool addresses provider

// Simplified TydroProtocolDataProvider ABI - just what we need for stats
const TYDRO_DATA_PROVIDER_ABI = [
  {
    name: 'getAllReservesTokens',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [
      {
        name: '',
        type: 'tuple[]',
        components: [
          { name: 'symbol', type: 'string' },
          { name: 'tokenAddress', type: 'address' }
        ]
      }
    ]
  },
  {
    name: 'getReserveData',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'asset', type: 'address' }],
    outputs: [
      { name: 'availableLiquidity', type: 'uint256' },
      { name: 'totalStableDebt', type: 'uint256' },
      { name: 'totalVariableDebt', type: 'uint256' },
      { name: 'liquidityRate', type: 'uint256' },
      { name: 'variableBorrowRate', type: 'uint256' },
      { name: 'stableBorrowRate', type: 'uint256' },
      { name: 'averageStableBorrowRate', type: 'uint256' },
      { name: 'liquidityIndex', type: 'uint256' },
      { name: 'variableBorrowIndex', type: 'uint256' },
      { name: 'lastUpdateTimestamp', type: 'uint40' }
    ]
  }
] as const;

// WETHGateway ABI for depositing native ETH
const WETH_GATEWAY_ABI = [
  {
    name: 'depositETH',
    type: 'function',
    stateMutability: 'payable',
    inputs: [
      { name: 'pool', type: 'address' },
      { name: 'onBehalfOf', type: 'address' },
      { name: 'referralCode', type: 'uint16' }
    ],
    outputs: []
  }
] as const;

// Tydro Pool ABI (minimal required functions)
const TYDRO_POOL_ABI = [
  {
    name: 'supply',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'asset', type: 'address' },
      { name: 'amount', type: 'uint256' },
      { name: 'onBehalfOf', type: 'address' },
      { name: 'referralCode', type: 'uint16' }
    ],
    outputs: []
  },
  {
    name: 'borrow',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'asset', type: 'address' },
      { name: 'amount', type: 'uint256' },
      { name: 'interestRateMode', type: 'uint256' },
      { name: 'referralCode', type: 'uint16' },
      { name: 'onBehalfOf', type: 'address' }
    ],
    outputs: []
  },
  {
    name: 'repay',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'asset', type: 'address' },
      { name: 'amount', type: 'uint256' },
      { name: 'interestRateMode', type: 'uint256' },
      { name: 'onBehalfOf', type: 'address' }
    ],
    outputs: [{ name: '', type: 'uint256' }]
  },
  {
    name: 'withdraw',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'asset', type: 'address' },
      { name: 'amount', type: 'uint256' },
      { name: 'to', type: 'address' }
    ],
    outputs: [{ name: '', type: 'uint256' }]
  },
  {
    name: 'getUserAccountData',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'user', type: 'address' }],
    outputs: [
      { name: 'totalCollateralBase', type: 'uint256' },
      { name: 'totalDebtBase', type: 'uint256' },
      { name: 'availableBorrowsBase', type: 'uint256' },
      { name: 'currentLiquidationThreshold', type: 'uint256' },
      { name: 'ltv', type: 'uint256' },
      { name: 'healthFactor', type: 'uint256' }
    ]
  },
  {
    name: 'getReserveData',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'asset', type: 'address' }],
    outputs: [
      {
        name: 'configuration',
        type: 'tuple',
        components: [{ name: 'data', type: 'uint256' }]
      },
      { name: 'liquidityIndex', type: 'uint128' },
      { name: 'variableBorrowIndex', type: 'uint128' },
      { name: 'currentLiquidityRate', type: 'uint128' },
      { name: 'currentVariableBorrowRate', type: 'uint128' },
      { name: 'currentStableBorrowRate', type: 'uint128' },
      { name: 'lastUpdateTimestamp', type: 'uint40' },
      { name: 'aTokenAddress', type: 'address' },
      { name: 'stableDebtTokenAddress', type: 'address' },
      { name: 'variableDebtTokenAddress', type: 'address' },
      { name: 'interestRateStrategyAddress', type: 'address' },
      { name: 'id', type: 'uint8' }
    ]
  }
] as const;

interface UserAccountData {
  totalCollateralUSD: number;
  totalDebtUSD: number;
  availableBorrowsUSD: number;
  currentLiquidationThreshold: number;
  ltv: number;
  healthFactor: number;
}

interface ReserveData {
  borrowAPR: number;
  supplyAPY: number;
  availableLiquidity: number;
}

interface AssetRates {
  symbol: string;
  borrowAPY: string;
  supplyAPY: string;
  availableLiquidity: string;
  totalBorrowed: string;
}

interface ProtocolStats {
  totalMarketSizeUSD: number;
  totalAvailableUSD: number;
  totalBorrowsUSD: number;
  usdt0BorrowedUSD: number; // Specific USDT0 borrows
  kbtcBorrowedUSD: number; // Specific kBTC borrows
  assetRates: { [key: string]: AssetRates }; // Per-asset APY data
}

export const useTydro = () => {
  const { address } = useAccount();
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();

  const [userAccountData, setUserAccountData] = useState<UserAccountData | null>(null);
  const [usdtReserveData, setUsdtReserveData] = useState<ReserveData | null>(null);
  const [protocolStats, setProtocolStats] = useState<ProtocolStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch user's account data from Tydro
  const fetchUserAccountData = useCallback(async () => {
    if (!address || !publicClient) return;

    try {
      setLoading(true);
      setError(null);

      const data = await publicClient.readContract({
        address: TYDRO_POOL_ADDRESS,
        abi: TYDRO_POOL_ABI,
        functionName: 'getUserAccountData',
        args: [address]
      });

      // Convert from base units (8 decimals for USD in Tydro)
      const accountData: UserAccountData = {
        totalCollateralUSD: Number(formatUnits(data[0], 8)),
        totalDebtUSD: Number(formatUnits(data[1], 8)),
        availableBorrowsUSD: Number(formatUnits(data[2], 8)),
        currentLiquidationThreshold: Number(data[3]) / 100, // Convert from basis points
        ltv: Number(data[4]) / 100, // Convert from basis points
        healthFactor: Number(formatUnits(data[5], 18))
      };

      console.log('📊 [Tydro] User account data:', accountData);
      setUserAccountData(accountData);
    } catch (err: any) {
      console.error('❌ [Tydro] Error fetching user account data:', err);
      setError(err.message || 'Failed to fetch account data');
    } finally {
      setLoading(false);
    }
  }, [address, publicClient]);

  // Fetch USDT0 reserve data (APR, liquidity, etc.)
  const fetchUSDTReserveData = useCallback(async () => {
    if (!publicClient) return;

    try {
      const data = await publicClient.readContract({
        address: TYDRO_POOL_ADDRESS,
        abi: TYDRO_POOL_ABI,
        functionName: 'getReserveData',
        args: [USDT0_ADDRESS]
      });

      // Convert borrow rate from ray (27 decimals) to APR percentage
      const borrowRate = Number(formatUnits(data[4], 27)) * 100;
      const supplyRate = Number(formatUnits(data[3], 27)) * 100;

      const reserveData: ReserveData = {
        borrowAPR: borrowRate,
        supplyAPY: supplyRate,
        availableLiquidity: 0 // Would need to query aToken balance
      };

      console.log('📊 [Tydro] USDT0 reserve data:', reserveData);
      setUsdtReserveData(reserveData);
    } catch (err: any) {
      console.error('❌ [Tydro] Error fetching reserve data:', err);
    }
  }, [publicClient]);

  // Fetch protocol-wide stats using Aave SDK
  const fetchProtocolStats = useCallback(async () => {
    try {
      console.log('📊 [Tydro] Fetching real-time protocol stats from blockchain...');

      // Create ethers v5 provider for Ink network (required by Aave SDK)
      const ethersProvider = new ethers.providers.JsonRpcProvider('https://rpc-gel.inkonchain.com/');
      
      // Initialize UiPoolDataProvider from Aave SDK with ethers v5
      const poolDataProviderContract = new UiPoolDataProvider({
        uiPoolDataProviderAddress: UI_POOL_DATA_PROVIDER,
        provider: ethersProvider,
        chainId: 57073
      });

      // Fetch BOTH humanized (for easy parsing) AND raw data (for debt fields)
      const [reservesHumanized, reservesRaw] = await Promise.all([
        poolDataProviderContract.getReservesHumanized({
          lendingPoolAddressProvider: POOL_ADDRESSES_PROVIDER
        }),
        poolDataProviderContract.getReservesData({
          lendingPoolAddressProvider: POOL_ADDRESSES_PROVIDER
        })
      ]);

      // Calculate totals directly from raw reserve data
      let totalMarketSizeUSD = 0;
      let totalBorrowsUSD = 0;
      let usdt0BorrowedUSD = 0; // Track USDT0 specifically
      let kbtcBorrowedUSD = 0; // Track kBTC specifically
      const assetRates: { [key: string]: AssetRates } = {}; // Store per-asset APY data

      // Get base currency price in USD from Tydro oracle (8-decimal format)
      const baseCurrencyPriceUSD = parseFloat(reservesHumanized.baseCurrencyData.marketReferenceCurrencyPriceInUsd);

      // Process each reserve - combine humanized data with raw data for debt
      reservesHumanized.reservesData.forEach((reserve: any, index: number) => {
        const rawReserve = (reservesRaw as any)[0]?.[index];

        // Parse values from humanized and raw data
        const availableLiquidity = parseFloat(reserve.availableLiquidity || '0');
        const decimals = Number(reserve.decimals);
        const priceInMarketReferenceCurrency = parseFloat(reserve.priceInMarketReferenceCurrency || '0');
        
        // Get debt from raw data using totalScaledVariableDebt field (stable debt is 0 on Tydro)
        const totalVariableDebt = rawReserve?.totalScaledVariableDebt ? 
          parseFloat(rawReserve.totalScaledVariableDebt.toString()) : 0;

        // Convert to human-readable amounts
        const liquidityAmount = availableLiquidity / Math.pow(10, decimals);
        const debtAmount = totalVariableDebt / Math.pow(10, decimals);

        // Aave oracle prices are in 8-decimal format
        // priceInMarketReferenceCurrency is the token price in base currency (ETH) with 8 decimals
        // baseCurrencyPriceUSD is the USD price of base currency with 8 decimals
        // To get USD price per token: (tokenPriceInETH / 10^8) * (ethPriceInUSD / 10^8)
        const pricePerToken = (priceInMarketReferenceCurrency / Math.pow(10, 8)) * (baseCurrencyPriceUSD / Math.pow(10, 8));

        // Calculate USD values
        const liquidityUSD = liquidityAmount * pricePerToken;
        const debtUSD = debtAmount * pricePerToken;

        totalMarketSizeUSD += (liquidityUSD + debtUSD); // Total market = available + borrowed
        totalBorrowsUSD += debtUSD;

        // Extract APY data from reserve
        // Aave rates are in RAY format (1e27) and already represent annual rates
        // Simple conversion: (RAY / 1e27) * 100 = percentage
        const variableBorrowRateRay = reserve.variableBorrowRate || '0';
        const liquidityRateRay = reserve.liquidityRate || '0';
        
        // Convert from RAY to percentage
        const borrowAPYPercent = (parseFloat(variableBorrowRateRay) / 1e27) * 100;
        const supplyAPYPercent = (parseFloat(liquidityRateRay) / 1e27) * 100;
        
        // Normalize symbol for consistent access (USD₮0 -> USDT0)
        const normalizedSymbol = reserve.symbol === 'USD₮0' ? 'USDT0' : reserve.symbol;
        
        // Store per-asset rates for UI display
        assetRates[normalizedSymbol] = {
          symbol: normalizedSymbol,
          borrowAPY: borrowAPYPercent.toFixed(2),
          supplyAPY: supplyAPYPercent.toFixed(2),
          availableLiquidity: liquidityAmount.toFixed(4),
          totalBorrowed: debtAmount.toFixed(4)
        };

        // Capture USDT0 borrows specifically
        if (reserve.symbol === 'USD₮0' || reserve.symbol === 'USDT0') {
          usdt0BorrowedUSD = debtUSD;
          console.log(`💚 [Tydro] USDT0 BORROWED: $${(debtUSD / 1000000).toFixed(2)}M | Borrow APY: ${assetRates[normalizedSymbol].borrowAPY}% | Supply APY: ${assetRates[normalizedSymbol].supplyAPY}%`);
        }

        // Capture kBTC borrows specifically
        if (reserve.symbol === 'kBTC') {
          kbtcBorrowedUSD = debtUSD;
          console.log(`🟡 [Tydro] kBTC BORROWED: $${(debtUSD / 1000000).toFixed(2)}M | Borrow APY: ${assetRates[normalizedSymbol].borrowAPY}% | Supply APY: ${assetRates[normalizedSymbol].supplyAPY}%`);
        }

        console.log(`📊 [Tydro] ${reserve.symbol}: Liquidity=$${(liquidityUSD / 1000000).toFixed(2)}M, Debt=$${(debtUSD / 1000000).toFixed(2)}M, Price=$${pricePerToken.toFixed(2)}`);
      });

      const stats: ProtocolStats = {
        totalMarketSizeUSD,
        totalAvailableUSD: totalMarketSizeUSD - totalBorrowsUSD,
        totalBorrowsUSD,
        usdt0BorrowedUSD,
        kbtcBorrowedUSD,
        assetRates
      };

      console.log(`✅ [Tydro] LIVE Stats → Market: $${(totalMarketSizeUSD / 1000000).toFixed(2)}M | Available: $${((totalMarketSizeUSD - totalBorrowsUSD) / 1000000).toFixed(2)}M | Borrows: $${(totalBorrowsUSD / 1000000).toFixed(2)}M`);
      setProtocolStats(stats);
    } catch (err: any) {
      console.error('❌ [Tydro] Error fetching protocol stats:', err);
      // Silently fall back to approximate values if blockchain fetch fails
      setProtocolStats({
        totalMarketSizeUSD: 406270000,
        totalAvailableUSD: 272660000,
        totalBorrowsUSD: 133610000,
        usdt0BorrowedUSD: 72750000, // Approximate USDT0 borrows from Nov 4 data
        kbtcBorrowedUSD: 350000, // Approximate kBTC borrows
        assetRates: {
          'USD₮0': {
            symbol: 'USD₮0',
            borrowAPY: '3.00',
            supplyAPY: '100.15',
            availableLiquidity: '82060000',
            totalBorrowed: '72750000'
          },
          'kBTC': {
            symbol: 'kBTC',
            borrowAPY: '0.15',
            supplyAPY: '0.08',
            availableLiquidity: '86050000',
            totalBorrowed: '350000'
          },
          'WETH': {
            symbol: 'WETH',
            borrowAPY: '0.14',
            supplyAPY: '0.05',
            availableLiquidity: '101280000',
            totalBorrowed: '33190000'
          }
        }
      });
    }
  }, []);

  // Calculate health factor for a potential borrow
  const calculateHealthFactor = useCallback(
    (collateralETH: number, borrowUSDT: number, ethPrice: number): number => {
      if (!collateralETH || !borrowUSDT || !ethPrice) return 0;

      // Tydro typically uses 85% liquidation threshold for WETH
      const liquidationThreshold = 0.85;
      const collateralValue = collateralETH * ethPrice;
      const collateralValueWithThreshold = collateralValue * liquidationThreshold;

      // Health Factor = (Collateral × LiquidationThreshold) / Debt
      const healthFactor = collateralValueWithThreshold / borrowUSDT;

      return healthFactor;
    },
    []
  );

  // Calculate safe borrow amount (50% LTV for safety)
  const calculateSafeBorrowAmount = useCallback(
    (collateralETH: number, ethPrice: number): number => {
      if (!collateralETH || !ethPrice) return 0;

      const collateralValue = collateralETH * ethPrice;
      // Conservative 50% LTV
      const safeBorrowAmount = collateralValue * 0.50;

      return safeBorrowAmount;
    },
    []
  );

  // Calculate liquidation price
  const calculateLiquidationPrice = useCallback(
    (collateralETH: number, borrowUSDT: number): number => {
      if (!collateralETH || !borrowUSDT) return 0;

      // Liquidation happens when HF < 1
      // HF = (Collateral × 0.85 × Price) / Debt
      // At liquidation: (Collateral × 0.85 × Price) = Debt
      // Price = Debt / (Collateral × 0.85)

      const liquidationPrice = borrowUSDT / (collateralETH * 0.85);

      return liquidationPrice;
    },
    []
  );

  // Supply ETH as collateral using WETHGateway
  const supplyETHCollateral = useCallback(
    async (amountETH: string) => {
      if (!walletClient || !address) {
        throw new Error('Wallet not connected');
      }

      try {
        setLoading(true);
        setError(null);

        const amount = parseUnits(amountETH, 18);

        console.log('🏦 [Tydro] Supplying native ETH collateral via WETHGateway:', amountETH);
        console.log('🏦 [Tydro] Amount in wei:', amount.toString());

        // Use WETHGateway to deposit native ETH (wraps + deposits in one tx)
        const hash = await walletClient.writeContract({
          address: WETH_GATEWAY,
          abi: WETH_GATEWAY_ABI,
          functionName: 'depositETH',
          args: [
            TYDRO_POOL_ADDRESS, // pool address
            address,            // onBehalfOf
            0                   // referralCode
          ],
          value: amount         // Send native ETH as msg.value
        });

        console.log('✅ [Tydro] Supply transaction sent:', hash);
        console.log('🔗 [Tydro] Transaction:', `https://explorer.inkonchain.com/tx/${hash}`);

        // Wait for confirmation
        if (publicClient) {
          const receipt = await publicClient.waitForTransactionReceipt({ hash });
          console.log('✅ [Tydro] Supply confirmed, status:', receipt.status);
          
          if (receipt.status === 'success') {
            // Refresh account data
            await fetchUserAccountData();
          } else {
            console.error('❌ [Tydro] Transaction reverted on-chain');
            console.error('❌ [Tydro] This might be due to WETH supply cap being reached on Tydro protocol');
            throw new Error('Supply failed - WETH supply cap may be reached. Try a smaller amount or use kBTC as collateral.');
          }
        }

        return hash;
      } catch (err: any) {
        console.error('❌ [Tydro] Supply error:', err);
        console.error('❌ [Tydro] Error details:', {
          message: err.message,
          code: err.code,
          data: err.data
        });
        setError(err.message || 'Failed to supply collateral');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [walletClient, address, publicClient, fetchUserAccountData]
  );

  // Supply WETH directly to Pool (no wrapping needed)
  const supplyWETHCollateral = useCallback(
    async (amountWETH: string) => {
      if (!walletClient || !address) {
        throw new Error('Wallet not connected');
      }

      try {
        setLoading(true);
        setError(null);

        const amount = parseUnits(amountWETH, 18);

        console.log('🏦 [Tydro] Supplying WETH collateral via Pool.supply:', amountWETH);
        console.log('🏦 [Tydro] Amount in wei:', amount.toString());

        // Check WETH balance first
        if (publicClient) {
          const wethBalance = await publicClient.readContract({
            address: WETH_ADDRESS,
            abi: [
              {
                inputs: [{ name: 'account', type: 'address' }],
                name: 'balanceOf',
                outputs: [{ name: '', type: 'uint256' }],
                stateMutability: 'view',
                type: 'function'
              }
            ],
            functionName: 'balanceOf',
            args: [address]
          });

        console.log('💰 [Tydro] Your WETH balance:', formatUnits(wethBalance as bigint, 18));
        
        if ((wethBalance as bigint) < amount) {
          throw new Error(`Insufficient WETH balance. You have ${formatUnits(wethBalance as bigint, 18)} WETH, trying to supply ${amountWETH} WETH`);
        }

        // Check existing allowance
        const existingAllowance = await publicClient.readContract({
          address: WETH_ADDRESS,
          abi: [
            {
              name: 'allowance',
              type: 'function',
              stateMutability: 'view',
              inputs: [
                { name: 'owner', type: 'address' },
                { name: 'spender', type: 'address' }
              ],
              outputs: [{ name: '', type: 'uint256' }]
            }
          ],
          functionName: 'allowance',
          args: [address, TYDRO_POOL_ADDRESS]
        });

        console.log('🔍 [Tydro] Existing WETH allowance:', formatUnits(existingAllowance as bigint, 18));

        // Only approve if needed
        if ((existingAllowance as bigint) < amount) {
          console.log('📝 [Tydro] Approving WETH (insufficient allowance)...');
          
          // Step 1: Approve WETH to Pool (use max for convenience)
          const approveAmount = BigInt('0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff');
          const approveHash = await walletClient.writeContract({
          address: WETH_ADDRESS,
          abi: [
            {
              name: 'approve',
              type: 'function',
              stateMutability: 'nonpayable',
              inputs: [
                { name: 'spender', type: 'address' },
                { name: 'amount', type: 'uint256' }
              ],
              outputs: [{ name: '', type: 'bool' }]
            }
          ],
          functionName: 'approve',
          args: [TYDRO_POOL_ADDRESS, approveAmount]
        });

        console.log('✅ [Tydro] WETH approved (max):', approveHash);

        if (publicClient) {
          await publicClient.waitForTransactionReceipt({ hash: approveHash });
          console.log('✅ [Tydro] WETH approval confirmed');
          
          // Wait for approval to settle on-chain (increased from 1s to 3s)
          await new Promise(resolve => setTimeout(resolve, 3000));
          
          // Verify the allowance was set correctly
          const allowance = await publicClient.readContract({
            address: WETH_ADDRESS,
            abi: [
              {
                name: 'allowance',
                type: 'function',
                stateMutability: 'view',
                inputs: [
                  { name: 'owner', type: 'address' },
                  { name: 'spender', type: 'address' }
                ],
                outputs: [{ name: '', type: 'uint256' }]
              }
            ],
            functionName: 'allowance',
            args: [address, TYDRO_POOL_ADDRESS]
          });
          
            console.log('✅ [Tydro] Verified allowance:', formatUnits(allowance as bigint, 18), 'WETH');
            
            if ((allowance as bigint) < amount) {
              throw new Error(`Allowance not set correctly. Expected >= ${amountWETH} WETH, got ${formatUnits(allowance as bigint, 18)} WETH`);
            }
          }
        } else {
          console.log('✅ [Tydro] Sufficient WETH allowance already exists, skipping approval');
        }
      }

      // Step 2: Supply WETH to Pool
        console.log('🏦 [Tydro] Now attempting Pool.supply...');
        const hash = await walletClient.writeContract({
          address: TYDRO_POOL_ADDRESS,
          abi: TYDRO_POOL_ABI,
          functionName: 'supply',
          args: [
            WETH_ADDRESS, // asset
            amount,       // amount
            address,      // onBehalfOf
            0            // referralCode
          ]
        });

        console.log('✅ [Tydro] WETH supply transaction sent:', hash);
        console.log('🔗 [Tydro] Transaction:', `https://explorer.inkonchain.com/tx/${hash}`);

        if (publicClient) {
          const receipt = await publicClient.waitForTransactionReceipt({ hash });
          console.log('✅ [Tydro] WETH supply confirmed, status:', receipt.status);
          
          if (receipt.status === 'success') {
            await fetchUserAccountData();
          } else {
            console.error('❌ [Tydro] Transaction reverted on-chain');
            console.error('❌ [Tydro] This might be due to WETH supply cap being reached on Tydro protocol');
            throw new Error('Supply failed - WETH supply cap may be reached. Try native ETH instead, or use kBTC as collateral.');
          }
        }

        return hash;
      } catch (err: any) {
        console.error('❌ [Tydro] WETH Supply error:', err);
        console.error('❌ [Tydro] Full error object:', {
          message: err.message,
          shortMessage: err.shortMessage,
          code: err.code,
          data: err.data,
          cause: err.cause
        });
        
        // Better error messages
        let userMessage = 'Failed to supply WETH';
        if (err.message?.includes('insufficient funds')) {
          userMessage = 'Insufficient WETH balance';
        } else if (err.message?.includes('user rejected') || err.message?.includes('user denied')) {
          userMessage = 'Transaction cancelled';
        } else if (err.shortMessage) {
          userMessage = err.shortMessage;
        }
        
        setError(userMessage);
        throw new Error(userMessage);
      } finally {
        setLoading(false);
      }
    },
    [walletClient, address, publicClient, fetchUserAccountData]
  );

  // Generic borrow function (USDT0 or kBTC)
  const borrowAsset = useCallback(
    async (assetAddress: Address, amount: string, decimals: number, assetName: string) => {
      if (!walletClient || !address) {
        throw new Error('Wallet not connected');
      }

      try {
        setLoading(true);
        setError(null);

        const amountBigInt = parseUnits(amount, decimals);

        console.log(`💰 [Tydro] Borrowing ${assetName}:`, amount);

        const hash = await walletClient.writeContract({
          address: TYDRO_POOL_ADDRESS,
          abi: TYDRO_POOL_ABI,
          functionName: 'borrow',
          args: [
            assetAddress,
            amountBigInt,
            2n, // variable interest rate mode
            0, // referral code
            address
          ]
        });

        console.log('✅ [Tydro] Borrow transaction sent:', hash);

        // Wait for confirmation
        if (publicClient) {
          await publicClient.waitForTransactionReceipt({ hash });
          console.log('✅ [Tydro] Borrow confirmed');
          
          // Refresh account data
          await fetchUserAccountData();
        }

        return hash;
      } catch (err: any) {
        console.error(`❌ [Tydro] Borrow error (${assetName}):`, err);
        setError(err.message || `Failed to borrow ${assetName}`);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [walletClient, address, publicClient, fetchUserAccountData]
  );

  // Supply USDT0 as collateral
  const supplyUSDT0Collateral = useCallback(
    async (amountUSDT0: string) => {
      if (!walletClient || !address) {
        throw new Error('Wallet not connected');
      }

      try {
        setLoading(true);
        setError(null);

        const amount = parseUnits(amountUSDT0, 6); // USDT0 has 6 decimals

        console.log('💚 [Tydro] Supplying USDT0 collateral via Pool.supply:', amountUSDT0);
        console.log('💚 [Tydro] Amount in micro-units:', amount.toString());

        // Check USDT0 balance first
        if (publicClient) {
          const usdt0Balance = await publicClient.readContract({
            address: USDT0_ADDRESS,
            abi: [
              {
                inputs: [{ name: 'account', type: 'address' }],
                name: 'balanceOf',
                outputs: [{ name: '', type: 'uint256' }],
                stateMutability: 'view',
                type: 'function'
              }
            ],
            functionName: 'balanceOf',
            args: [address]
          });

          console.log('💰 [Tydro] Your USDT0 balance:', formatUnits(usdt0Balance as bigint, 6));
          
          if ((usdt0Balance as bigint) < amount) {
            throw new Error(`Insufficient USDT0 balance. You have ${formatUnits(usdt0Balance as bigint, 6)} USDT0, trying to supply ${amountUSDT0} USDT0`);
          }

          // Check existing allowance
          const existingAllowance = await publicClient.readContract({
            address: USDT0_ADDRESS,
            abi: [
              {
                name: 'allowance',
                type: 'function',
                stateMutability: 'view',
                inputs: [
                  { name: 'owner', type: 'address' },
                  { name: 'spender', type: 'address' }
                ],
                outputs: [{ name: '', type: 'uint256' }]
              }
            ],
            functionName: 'allowance',
            args: [address, TYDRO_POOL_ADDRESS]
          });

          console.log('🔍 [Tydro] Existing USDT0 allowance:', formatUnits(existingAllowance as bigint, 6));

          // Only approve if needed
          if ((existingAllowance as bigint) < amount) {
            console.log('📝 [Tydro] Approving USDT0 (insufficient allowance)...');
            
            // Step 1: Approve USDT0 to Pool
            const approveAmount = BigInt('0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff');
            const approveHash = await walletClient.writeContract({
          address: USDT0_ADDRESS,
          abi: [
            {
              name: 'approve',
              type: 'function',
              stateMutability: 'nonpayable',
              inputs: [
                { name: 'spender', type: 'address' },
                { name: 'amount', type: 'uint256' }
              ],
              outputs: [{ name: '', type: 'bool' }]
            }
          ],
          functionName: 'approve',
          args: [TYDRO_POOL_ADDRESS, approveAmount]
        });

        console.log('✅ [Tydro] USDT0 approved (max):', approveHash);

        if (publicClient) {
          await publicClient.waitForTransactionReceipt({ hash: approveHash });
          console.log('✅ [Tydro] USDT0 approval confirmed');
          
          // Wait for approval to settle on-chain
          await new Promise(resolve => setTimeout(resolve, 3000));
          
          // Verify the allowance was set correctly
          const allowance = await publicClient.readContract({
            address: USDT0_ADDRESS,
            abi: [
              {
                name: 'allowance',
                type: 'function',
                stateMutability: 'view',
                inputs: [
                  { name: 'owner', type: 'address' },
                  { name: 'spender', type: 'address' }
                ],
                outputs: [{ name: '', type: 'uint256' }]
              }
            ],
            functionName: 'allowance',
            args: [address, TYDRO_POOL_ADDRESS]
          });
          
            console.log('✅ [Tydro] Verified allowance:', formatUnits(allowance as bigint, 6), 'USDT0');
            
            if ((allowance as bigint) < amount) {
              throw new Error(`Allowance not set correctly. Expected >= ${amountUSDT0} USDT0, got ${formatUnits(allowance as bigint, 6)} USDT0`);
            }
          }
        } else {
          console.log('✅ [Tydro] Sufficient USDT0 allowance already exists, skipping approval');
        }
      }

      // Step 2: Supply USDT0 to Pool
        console.log('💚 [Tydro] Now attempting Pool.supply...');
        const hash = await walletClient.writeContract({
          address: TYDRO_POOL_ADDRESS,
          abi: TYDRO_POOL_ABI,
          functionName: 'supply',
          args: [
            USDT0_ADDRESS, // asset
            amount,        // amount
            address,       // onBehalfOf
            0             // referralCode
          ]
        });

        console.log('✅ [Tydro] USDT0 supply transaction sent:', hash);
        console.log('🔗 [Tydro] Transaction:', `https://explorer.inkonchain.com/tx/${hash}`);

        if (publicClient) {
          const receipt = await publicClient.waitForTransactionReceipt({ hash });
          console.log('✅ [Tydro] USDT0 supply confirmed, status:', receipt.status);
          
          if (receipt.status === 'success') {
            await fetchUserAccountData();
          } else {
            console.error('❌ [Tydro] Transaction reverted on-chain');
            throw new Error('USDT0 supply failed - check transaction on explorer for details.');
          }
        }

        return hash;
      } catch (err: any) {
        console.error('❌ [Tydro] USDT0 Supply error:', err);
        console.error('❌ [Tydro] Full error object:', {
          message: err.message,
          shortMessage: err.shortMessage,
          code: err.code,
          data: err.data,
          cause: err.cause
        });
        
        // Better error messages
        let userMessage = 'Failed to supply USDT0';
        if (err.message?.includes('insufficient funds')) {
          userMessage = 'Insufficient USDT0 balance';
        } else if (err.message?.includes('user rejected') || err.message?.includes('user denied')) {
          userMessage = 'Transaction cancelled';
        } else if (err.shortMessage) {
          userMessage = err.shortMessage;
        }
        
        setError(userMessage);
        throw new Error(userMessage);
      } finally {
        setLoading(false);
      }
    },
    [walletClient, address, publicClient, fetchUserAccountData]
  );

  // Supply kBTC as collateral
  const supplyKBTCCollateral = useCallback(
    async (amountKBTC: string) => {
      if (!walletClient || !address) {
        throw new Error('Wallet not connected');
      }

      try {
        setLoading(true);
        setError(null);

        const amount = parseUnits(amountKBTC, 8); // kBTC has 8 decimals

        console.log('🟡 [Tydro] Supplying kBTC collateral via Pool.supply:', amountKBTC);
        console.log('🟡 [Tydro] Amount (satoshis):', amount.toString());

        // Check kBTC balance first
        if (publicClient) {
          const kbtcBalance = await publicClient.readContract({
            address: KBTC_ADDRESS,
            abi: [
              {
                inputs: [{ name: 'account', type: 'address' }],
                name: 'balanceOf',
                outputs: [{ name: '', type: 'uint256' }],
                stateMutability: 'view',
                type: 'function'
              }
            ],
            functionName: 'balanceOf',
            args: [address]
          });

          console.log('💰 [Tydro] Your kBTC balance:', formatUnits(kbtcBalance as bigint, 8));
          
          if ((kbtcBalance as bigint) < amount) {
            throw new Error(`Insufficient kBTC balance. You have ${formatUnits(kbtcBalance as bigint, 8)} kBTC, trying to supply ${amountKBTC} kBTC`);
          }

          // Check existing allowance
          const existingAllowance = await publicClient.readContract({
            address: KBTC_ADDRESS,
            abi: [
              {
                name: 'allowance',
                type: 'function',
                stateMutability: 'view',
                inputs: [
                  { name: 'owner', type: 'address' },
                  { name: 'spender', type: 'address' }
                ],
                outputs: [{ name: '', type: 'uint256' }]
              }
            ],
            functionName: 'allowance',
            args: [address, TYDRO_POOL_ADDRESS]
          });

          console.log('🔍 [Tydro] Existing kBTC allowance:', formatUnits(existingAllowance as bigint, 8));

          // Only approve if needed
          if ((existingAllowance as bigint) < amount) {
            console.log('📝 [Tydro] Approving kBTC (insufficient allowance)...');
            
            // Step 1: Approve kBTC to Pool (use max for convenience)
            const approveAmount = BigInt('0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff');
            const approveHash = await walletClient.writeContract({
          address: KBTC_ADDRESS,
          abi: [
            {
              name: 'approve',
              type: 'function',
              stateMutability: 'nonpayable',
              inputs: [
                { name: 'spender', type: 'address' },
                { name: 'amount', type: 'uint256' }
              ],
              outputs: [{ name: '', type: 'bool' }]
            }
          ],
          functionName: 'approve',
          args: [TYDRO_POOL_ADDRESS, approveAmount]
        });

        console.log('✅ [Tydro] kBTC approved (max):', approveHash);

        if (publicClient) {
          await publicClient.waitForTransactionReceipt({ hash: approveHash });
          console.log('✅ [Tydro] kBTC approval confirmed');
          
          // Wait for approval to settle on-chain
          await new Promise(resolve => setTimeout(resolve, 3000));
          
          // Verify the allowance was set correctly
          const allowance = await publicClient.readContract({
            address: KBTC_ADDRESS,
            abi: [
              {
                name: 'allowance',
                type: 'function',
                stateMutability: 'view',
                inputs: [
                  { name: 'owner', type: 'address' },
                  { name: 'spender', type: 'address' }
                ],
                outputs: [{ name: '', type: 'uint256' }]
              }
            ],
            functionName: 'allowance',
            args: [address, TYDRO_POOL_ADDRESS]
          });
          
            console.log('✅ [Tydro] Verified allowance:', formatUnits(allowance as bigint, 8), 'kBTC');
            
            if ((allowance as bigint) < amount) {
              throw new Error(`Allowance not set correctly. Expected >= ${amountKBTC} kBTC, got ${formatUnits(allowance as bigint, 8)} kBTC`);
            }
          }
        } else {
          console.log('✅ [Tydro] Sufficient kBTC allowance already exists, skipping approval');
        }
      }

      // Step 2: Supply kBTC to Pool
        console.log('🟡 [Tydro] Now attempting Pool.supply...');
        const hash = await walletClient.writeContract({
          address: TYDRO_POOL_ADDRESS,
          abi: TYDRO_POOL_ABI,
          functionName: 'supply',
          args: [
            KBTC_ADDRESS, // asset
            amount,       // amount
            address,      // onBehalfOf
            0            // referralCode
          ]
        });

        console.log('✅ [Tydro] kBTC supply transaction sent:', hash);
        console.log('🔗 [Tydro] Transaction:', `https://explorer.inkonchain.com/tx/${hash}`);

        if (publicClient) {
          const receipt = await publicClient.waitForTransactionReceipt({ hash });
          console.log('✅ [Tydro] kBTC supply confirmed, status:', receipt.status);
          
          if (receipt.status === 'success') {
            await fetchUserAccountData();
          } else {
            console.error('❌ [Tydro] Transaction reverted on-chain');
            throw new Error('kBTC supply failed - check transaction on explorer for details.');
          }
        }

        return hash;
      } catch (err: any) {
        console.error('❌ [Tydro] kBTC Supply error:', err);
        console.error('❌ [Tydro] Full error object:', {
          message: err.message,
          shortMessage: err.shortMessage,
          code: err.code,
          data: err.data,
          cause: err.cause
        });
        
        // Better error messages
        let userMessage = 'Failed to supply kBTC';
        if (err.message?.includes('insufficient funds')) {
          userMessage = 'Insufficient kBTC balance';
        } else if (err.message?.includes('user rejected') || err.message?.includes('user denied')) {
          userMessage = 'Transaction cancelled';
        } else if (err.shortMessage) {
          userMessage = err.shortMessage;
        }
        
        setError(userMessage);
        throw new Error(userMessage);
      } finally {
        setLoading(false);
      }
    },
    [walletClient, address, publicClient, fetchUserAccountData]
  );

  // Backwards-compatible USDT0 borrow function
  const borrowUSDT = useCallback(
    async (amountUSDT: string) => {
      return borrowAsset(USDT0_ADDRESS, amountUSDT, 6, 'USDT0');
    },
    [borrowAsset]
  );

  // Repay USDT0 debt
  const repayUSDT = useCallback(
    async (amountUSDT: string, repayAll: boolean = false) => {
      if (!walletClient || !address || !publicClient) {
        throw new Error('Wallet not connected');
      }

      try {
        setLoading(true);
        setError(null);

        // Check user's USDT0 balance
        const usdtBalance = await publicClient.readContract({
          address: USDT0_ADDRESS,
          abi: [
            {
              name: 'balanceOf',
              type: 'function',
              stateMutability: 'view',
              inputs: [{ name: 'account', type: 'address' }],
              outputs: [{ name: '', type: 'uint256' }]
            }
          ],
          functionName: 'balanceOf',
          args: [address]
        }) as bigint;

        const balanceNum = parseFloat(formatUnits(usdtBalance, 6));
        console.log('💰 [Tydro] User USDT0 balance:', balanceNum);

        // If repaying all, check if user has enough balance
        if (repayAll) {
          // Fetch debt balance directly (to avoid circular dependency)
          const variableDebtUSDT0 = getAddress('0xa5d61ef3d06234371507e54fb709f401e590719a');
          const debtAmount = await publicClient.readContract({
            address: variableDebtUSDT0,
            abi: [
              {
                inputs: [{ name: 'user', type: 'address' }],
                name: 'balanceOf',
                outputs: [{ name: '', type: 'uint256' }],
                stateMutability: 'view',
                type: 'function'
              }
            ],
            functionName: 'balanceOf',
            args: [address]
          }) as bigint;
          
          const debtNum = parseFloat(formatUnits(debtAmount, 6));
          
          console.log('💸 [Tydro] Repaying ALL DEBT - Debt:', debtNum, 'Balance:', balanceNum);
          
          if (balanceNum < debtNum) {
            throw new Error(`Insufficient USDT0 balance. You need ${debtNum.toFixed(6)} USDT0 but only have ${balanceNum.toFixed(6)} USDT0 in your wallet.`);
          }
          
          // Use exact debt amount instead of uint256.max to avoid issues
          const amount = debtAmount;
          
          console.log('💸 [Tydro] Repaying exact debt amount:', formatUnits(amount, 6));

          // Approve exact amount
          const approveHash = await walletClient.writeContract({
            address: USDT0_ADDRESS,
            abi: [
              {
                name: 'approve',
                type: 'function',
                stateMutability: 'nonpayable',
                inputs: [
                  { name: 'spender', type: 'address' },
                  { name: 'amount', type: 'uint256' }
                ],
                outputs: [{ name: '', type: 'bool' }]
              }
            ],
            functionName: 'approve',
            args: [TYDRO_POOL_ADDRESS, amount]
          });

          console.log('✅ [Tydro] USDT0 approved:', approveHash);
          await publicClient.waitForTransactionReceipt({ hash: approveHash });

          // Repay with exact amount
          const repayHash = await walletClient.writeContract({
            address: TYDRO_POOL_ADDRESS,
            abi: TYDRO_POOL_ABI,
            functionName: 'repay',
            args: [
              USDT0_ADDRESS,
              amount,
              2n, // variable interest rate mode
              address
            ]
          });

          console.log('✅ [Tydro] Repay transaction sent:', repayHash);
          await publicClient.waitForTransactionReceipt({ hash: repayHash });
          console.log('✅ [Tydro] Repay confirmed');
          await fetchUserAccountData();
          return repayHash;
        } else {
          // Partial repayment
          const amount = parseUnits(amountUSDT, 6);
          const amountNum = parseFloat(amountUSDT);
          
          console.log('💸 [Tydro] Repaying partial amount:', amountNum);
          
          if (balanceNum < amountNum) {
            throw new Error(`Insufficient USDT0 balance. You need ${amountNum.toFixed(6)} USDT0 but only have ${balanceNum.toFixed(6)} USDT0 in your wallet.`);
          }

          // Approve amount
          const approveHash = await walletClient.writeContract({
            address: USDT0_ADDRESS,
            abi: [
              {
                name: 'approve',
                type: 'function',
                stateMutability: 'nonpayable',
                inputs: [
                  { name: 'spender', type: 'address' },
                  { name: 'amount', type: 'uint256' }
                ],
                outputs: [{ name: '', type: 'bool' }]
              }
            ],
            functionName: 'approve',
            args: [TYDRO_POOL_ADDRESS, amount]
          });

          console.log('✅ [Tydro] USDT0 approved:', approveHash);
          await publicClient.waitForTransactionReceipt({ hash: approveHash });

          // Repay
          const repayHash = await walletClient.writeContract({
            address: TYDRO_POOL_ADDRESS,
            abi: TYDRO_POOL_ABI,
            functionName: 'repay',
            args: [
              USDT0_ADDRESS,
              amount,
              2n, // variable interest rate mode
              address
            ]
          });

          console.log('✅ [Tydro] Repay transaction sent:', repayHash);
          await publicClient.waitForTransactionReceipt({ hash: repayHash });
          console.log('✅ [Tydro] Repay confirmed');
          await fetchUserAccountData();
          return repayHash;
        }
      } catch (err: any) {
        console.error('❌ [Tydro] Repay error:', err);
        const errorMessage = err.message || 'Failed to repay USDT';
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [walletClient, address, publicClient, fetchUserAccountData]
  );

  // Get user's aWETH balance
  const getATokenBalance = useCallback(
    async (): Promise<bigint> => {
      if (!publicClient || !address) return BigInt(0);

      try {
        // aWETH token address from Tydro
        const aWETH = getAddress('0x2b35ef056728baffac103e3b81cb029788006ef9');
        
        const balance = await publicClient.readContract({
          address: aWETH,
          abi: [
            {
              inputs: [{ name: 'account', type: 'address' }],
              name: 'balanceOf',
              outputs: [{ name: '', type: 'uint256' }],
              stateMutability: 'view',
              type: 'function'
            }
          ],
          functionName: 'balanceOf',
          args: [address]
        });

        console.log('💰 [Tydro] aWETH balance:', formatUnits(balance as bigint, 18));
        return balance as bigint;
      } catch (err) {
        console.error('❌ [Tydro] Error fetching aToken balance:', err);
        return BigInt(0);
      }
    },
    [publicClient, address]
  );

  // Get user's USDT0 debt balance (variable debt token)
  const getUSDT0DebtBalance = useCallback(
    async (): Promise<bigint> => {
      if (!publicClient || !address) return BigInt(0);

      try {
        // Variable debt USDT0 token address from Tydro
        const variableDebtUSDT0 = getAddress('0xa5d61ef3d06234371507e54fb709f401e590719a');
        
        const balance = await publicClient.readContract({
          address: variableDebtUSDT0,
          abi: [
            {
              inputs: [{ name: 'user', type: 'address' }],
              name: 'balanceOf',
              outputs: [{ name: '', type: 'uint256' }],
              stateMutability: 'view',
              type: 'function'
            }
          ],
          functionName: 'balanceOf',
          args: [address]
        });

        console.log('💳 [Tydro] USDT0 debt balance:', formatUnits(balance as bigint, 6));
        return balance as bigint;
      } catch (err) {
        console.error('❌ [Tydro] Error fetching debt balance:', err);
        return BigInt(0);
      }
    },
    [publicClient, address]
  );

  // Generic withdraw function for any asset
  const withdrawAsset = useCallback(
    async (assetAddress: Address, amount: string, decimals: number, assetName: string, withdrawAll: boolean = false) => {
      if (!walletClient || !address) {
        throw new Error('Wallet not connected');
      }

      try {
        setLoading(true);
        setError(null);

        // If withdrawing all, use max uint256 to withdraw full balance including dust
        const amountBigInt = withdrawAll
          ? BigInt('0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff') // type(uint256).max
          : parseUnits(amount, decimals);

        console.log(`🏦 [Tydro] Withdrawing ${assetName} collateral via Pool.withdraw:`, withdrawAll ? 'ALL COLLATERAL (uint256.max)' : amount);

        // CRITICAL: Check if user has any debt
        // Tydro/Aave requires 0 debt to withdraw all collateral
        if (userAccountData && userAccountData.totalDebtUSD > 0 && !withdrawAll) {
          // Calculate how much we can safely withdraw while maintaining HF > 2
          const minCollateralNeeded = (userAccountData.totalDebtUSD / 0.85) * 2;
          const maxWithdrawUSD = Math.max(0, userAccountData.totalCollateralUSD - minCollateralNeeded);
          
          if (maxWithdrawUSD < 0.01) {
            throw new Error(
              `You have $${userAccountData.totalDebtUSD.toFixed(6)} debt. Please repay all debt before withdrawing collateral.`
            );
          }
        }

        // Direct Pool.withdraw
        const hash = await walletClient.writeContract({
          address: TYDRO_POOL_ADDRESS,
          abi: TYDRO_POOL_ABI,
          functionName: 'withdraw',
          args: [
            assetAddress,  // asset
            amountBigInt,  // amount (uint256.max to withdraw all including dust)
            address        // to (recipient)
          ]
        });

        console.log(`✅ [Tydro] ${assetName} withdraw transaction sent:`, hash);

        if (publicClient) {
          await publicClient.waitForTransactionReceipt({ hash });
          console.log(`✅ [Tydro] Withdraw confirmed - ${assetName} received`);
          await fetchUserAccountData();
        }

        return hash;
      } catch (err: any) {
        console.error(`❌ [Tydro] ${assetName} Withdraw error:`, err);
        setError(err.message || `Failed to withdraw ${assetName}`);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [walletClient, address, publicClient, fetchUserAccountData, userAccountData]
  );

  // Backwards-compatible withdrawETH function
  const withdrawETH = useCallback(
    async (amountETH: string, withdrawAll: boolean = false) => {
      return withdrawAsset(WETH_ADDRESS, amountETH, 18, 'WETH', withdrawAll);
    },
    [withdrawAsset]
  );

  // Fetch data on mount and when address changes
  useEffect(() => {
    // Always fetch protocol stats
    fetchProtocolStats();
    
    if (address) {
      fetchUserAccountData();
      fetchUSDTReserveData();
    }
  }, [address, fetchUserAccountData, fetchUSDTReserveData, fetchProtocolStats]);

  return {
    // State
    userAccountData,
    usdtReserveData,
    protocolStats,
    loading,
    error,

    // Actions
    supplyETHCollateral,
    supplyWETHCollateral,
    supplyUSDT0Collateral,
    supplyKBTCCollateral,
    borrowUSDT,
    borrowAsset,
    repayUSDT,
    withdrawETH,
    withdrawAsset,
    getATokenBalance,
    getUSDT0DebtBalance,
    fetchUserAccountData,
    fetchUSDTReserveData,
    fetchProtocolStats,

    // Calculators
    calculateHealthFactor,
    calculateSafeBorrowAmount,
    calculateLiquidationPrice,

    // Constants
    WETH_ADDRESS,
    USDT0_ADDRESS,
    KBTC_ADDRESS,
    TYDRO_POOL_ADDRESS,
    WETH_GATEWAY
  };
};

